import pick from 'lodash/pick';

import Physical from '../Physical';
import SubMenuItem from '../SubMenuItem';
import ImageViewer from '../ImageViewer';
import ProjectNotes from '../ProjectNotes';
import Loader from '../Loader';

import { useIdCatalogSearch } from '../SideMenuSearchLane/context';

import Description from './components/Description';

import Characteristics from './components/Characteristics';
import ViewsAndLayoutsCompare from './components/ViewsAndLayoutsCompare';
import LocationOnFPSOCompare from './components/LocationOnFPSOCompare';

import ComparisonLane from './components/ComparisonLane';

import { PlusIcon } from '../CatalogListItem/styles';
import { useCompareManagement } from '../SideMenuSearchLane/context/compare';

import { ExitButton, SlotLoaderContainer } from '../IdCard/Module/styles';
import {
  BelowSection,
  Container,
  Content,
  FirstSection,
  Label,
  Left,
  Right,
  Section,
  InfoContainer,
  UpperSection,
  Header,
  ComparisonSubMenuWrapper,
  PrimaryImageContainer,
  CommandsWrapper,
  SecondSection,
  AddToCurrentSectionButton,
} from './styles';
import { IDCARD_SECTIONS_ENUM } from '../SideMenuSearchLane/context/compare/constants';
import { primaryColor } from '../../config/colors';

const IdCardCompareModeComponent = ({ itemData, canRenderComparisonCuts, onAddToLayoutClick }) => {
  const { idCard: idCardData } = itemData;
  const { removeCatalogItem, shouldRenderModuleItemAddIcon } = useIdCatalogSearch();
  const { comparisonState, onToggleSubMenuItem } = useCompareManagement();

  if (idCardData === undefined) {
    return (
      <SlotLoaderContainer>
        <Loader />
      </SlotLoaderContainer>
    );
  }

  return (
    <Container>
      <UpperSection>
        <FirstSection>
          <CommandsWrapper>
            <ExitButton onClick={() => removeCatalogItem(idCardData.tagId)} />
          </CommandsWrapper>
          <Left>
            <InfoContainer>
              <Label>Project:</Label>
              <Content>{`${idCardData.project ? idCardData.project : ''}`}</Content>
            </InfoContainer>
            <InfoContainer>
              <Label>Module:</Label>
              <Content>{`${idCardData.tsCode ?? ''} - ${idCardData.name}`}</Content>
            </InfoContainer>
            <InfoContainer>
              <Label>Family:</Label>
              <Content>{`${idCardData.family ? idCardData.family : ''}`}</Content>
            </InfoContainer>
            <InfoContainer>
              <Label>Secondary system:</Label>
              <Content>{`${idCardData.secondarySystem ? idCardData.secondarySystem : ''}`}</Content>
            </InfoContainer>
            <InfoContainer>
              <Label>Design maturity: </Label>
              <Content>{`${idCardData.designMaturity}`}</Content>
            </InfoContainer>
          </Left>
          <Right>
            <PrimaryImageContainer>
              <ImageViewer
                imageId={idCardData.images && idCardData.images.length > 0 ? idCardData.images[0].imageId : ''}
                fullscreen={false}
                defaultLoaderSize={75}
                containerStyle={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </PrimaryImageContainer>
          </Right>
        </FirstSection>
        {shouldRenderModuleItemAddIcon && (
          <SecondSection>
            <AddToCurrentSectionButton onClick={() => onAddToLayoutClick(idCardData)}>
              <PlusIcon
                backgroundColor={primaryColor}
                title='Add module'>
                +
              </PlusIcon>
              <label className='add-to-layout'>Add to current layout</label>
            </AddToCurrentSectionButton>
          </SecondSection>
        )}
        <Description description={idCardData.description} />

        <Characteristics characteristics={idCardData.characteristics} />
        <Section>
          <Header>Physical</Header>
          <Physical {...pick(idCardData, ['totalWeightDry', 'length', 'height', 'width'])} />
        </Section>
      </UpperSection>
      <BelowSection>
        <ComparisonSubMenuWrapper>
          <SubMenuItem
            isCollapsible={comparisonState.ViewsAndLayouts.isCollapsible}
            title={'Views And Layouts'}
            collapsed={comparisonState.ViewsAndLayouts.isCollapsed}
            component={<ViewsAndLayoutsCompare images={idCardData.images} />}
            onToggleCollapse={(isOpen) => {
              onToggleSubMenuItem(IDCARD_SECTIONS_ENUM.ViewsAndLayouts, isOpen);
            }}
          />
          {canRenderComparisonCuts && <ComparisonLane sectionId={IDCARD_SECTIONS_ENUM.ViewsAndLayouts} />}
        </ComparisonSubMenuWrapper>
        <ComparisonSubMenuWrapper>
          <SubMenuItem
            isCollapsible={comparisonState.WeightBreakdown.isCollapsible}
            title='Weight Breakdown'
          />
          {canRenderComparisonCuts && <ComparisonLane sectionId={IDCARD_SECTIONS_ENUM.WeightBreakdown} />}
        </ComparisonSubMenuWrapper>
        <ComparisonSubMenuWrapper>
          <SubMenuItem
            isCollapsible={comparisonState.TieIns.isCollapsible}
            title={'Main Tie-Ins & Interfaces'}
          />
          {canRenderComparisonCuts && <ComparisonLane sectionId={IDCARD_SECTIONS_ENUM.TieIns} />}
        </ComparisonSubMenuWrapper>
        <ComparisonSubMenuWrapper>
          <SubMenuItem
            isCollapsible={comparisonState.EquipmentList.isCollapsible}
            title={'Key Equipment List'}
          />
          {canRenderComparisonCuts && <ComparisonLane sectionId={IDCARD_SECTIONS_ENUM.EquipmentList} />}
        </ComparisonSubMenuWrapper>
        <ComparisonSubMenuWrapper>
          <SubMenuItem
            isCollapsible={comparisonState.DocumentList.isCollapsible}
            title={'Documentation List'}
          />
          {canRenderComparisonCuts && <ComparisonLane sectionId={IDCARD_SECTIONS_ENUM.DocumentList} />}
        </ComparisonSubMenuWrapper>
        <ComparisonSubMenuWrapper>
          <SubMenuItem
            isCollapsible={comparisonState.OtherDocumentList.isCollapsible}
            title={'Other Relevant Documents'}
          />
          {canRenderComparisonCuts && <ComparisonLane sectionId={IDCARD_SECTIONS_ENUM.OtherDocumentList} />}
        </ComparisonSubMenuWrapper>
        <ComparisonSubMenuWrapper>
          <SubMenuItem
            isCollapsible={comparisonState.LocationOnFPSO.isCollapsible}
            title={'Location on FPSO'}
            collapsed={comparisonState.LocationOnFPSO.isCollapsed}
            component={<LocationOnFPSOCompare images={idCardData.images} />}
            onToggleCollapse={(isOpen) => {
              onToggleSubMenuItem(IDCARD_SECTIONS_ENUM.LocationOnFPSO, isOpen);
            }}
          />
          {canRenderComparisonCuts && <ComparisonLane sectionId={IDCARD_SECTIONS_ENUM.LocationOnFPSO} />}
        </ComparisonSubMenuWrapper>

        <SubMenuItem
          title={'Project Notes'}
          component={<ProjectNotes resNotes={idCardData.resNotes} />}
        />
      </BelowSection>
    </Container>
  );
};

export default IdCardCompareModeComponent;
